import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import '@/App.css';
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import ProtectedRoute from '@/components/ProtectedRoute';

const Register = lazy(() => import('@/pages/Register'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const RegisterTeamNew = lazy(() => import('@/pages/RegisterTeamNew'));
const Teams = lazy(() => import('@/pages/Teams'));
const EdicaoYoutube = lazy(() => import('@/pages/EdicaoYoutube'));
const TeamDetail = lazy(() => import('@/pages/TeamDetail'));
const Standings = lazy(() => import('@/pages/Standings'));
const Statistics = lazy(() => import('@/pages/Statistics'));
const About = lazy(() => import('@/pages/About'));
const Rules = lazy(() => import('@/pages/Rules'));
const Discord = lazy(() => import('@/pages/Discord'));
const Knockout = lazy(() => import('@/pages/Knockout'));
const AdminGroups = lazy(() => import('@/pages/AdminGroups'));
const AdminSorteio = lazy(() => import('@/pages/AdminSorteio'));
const AdminMatches = lazy(() => import('@/pages/AdminMatches'));
const AdminTestData = lazy(() => import('@/pages/AdminTestData'));
const AdminContent = lazy(() => import('@/pages/AdminContent'));
const AdminRules = lazy(() => import('@/pages/AdminRules'));
const AdminRankings = lazy(() => import('@/pages/AdminRankings'));
const AdminDiscord = lazy(() => import('@/pages/AdminDiscord'));
const AdminKnockout = lazy(() => import('@/pages/AdminKnockout'));
const AdminSlotAssignment = lazy(() => import('@/pages/AdminSlotAssignment'));
const AdminTeamsNew = lazy(() => import('@/pages/AdminTeamsNew'));
const AdminSettings = lazy(() => import('@/pages/AdminSettings'));
const AdminDiagnostic = lazy(() => import('@/pages/AdminDiagnostic'));
const AdminReset = lazy(() => import('@/pages/AdminReset'));
const Confrontos = lazy(() => import('@/pages/Confrontos'));
const Rankings = lazy(() => import('@/pages/Rankings'));
const Payment = lazy(() => import('@/pages/Payment'));
const RenewSubscription = lazy(() => import('@/pages/RenewSubscription'));
const Perfil = lazy(() => import('@/pages/Perfil'));
const StatsAnalyzer = lazy(() => import('@/pages/StatsAnalyzer'));
const GroupChat = lazy(() => import('@/pages/GroupChat'));
const AdminChat = lazy(() => import('@/pages/AdminChat'));
const KnockoutChat = lazy(() => import('@/pages/KnockoutChat'));
const KnockoutChatInvite = lazy(() => import('@/pages/KnockoutChatInvite'));
const AdminKnockoutChat = lazy(() => import('@/pages/AdminKnockoutChat'));
const Apostados = lazy(() => import('@/pages/Apostados'));
const ApostadoRoom = lazy(() => import('@/pages/ApostadoRoom'));
const AdminApostados = lazy(() => import('@/pages/AdminApostados'));
const MeusApostados = lazy(() => import('@/pages/MeusApostados'));
const AdminCentral = lazy(() => import('@/pages/AdminCentral'));
const ApostadosDisponiveis = lazy(() => import('@/pages/ApostadosDisponiveis'));
const ApostadosRanking = lazy(() => import('@/pages/ApostadosRanking'));
const AdminRankingApostados = lazy(() => import('@/pages/AdminRankingApostados'));
const AdminPayments = lazy(() => import('@/pages/AdminPayments'));
const AdminWallets = lazy(() => import('@/pages/AdminWallets'));

export const API = '/api';

const PageLoader = () => (
  <div className="min-h-screen bg-obsidian flex items-center justify-center">
    <div className="text-gold-400 text-lg animate-pulse">Carregando...</div>
  </div>
);

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/teams" element={
        <ProtectedRoute>
          <Teams />
        </ProtectedRoute>
      } />
      <Route path="/teams/:teamId" element={
        <ProtectedRoute>
          <TeamDetail />
        </ProtectedRoute>
      } />
      <Route path="/groups" element={<Navigate to="/standings" replace />} />
      <Route path="/standings" element={<Standings />} />
      <Route path="/confrontos" element={<Confrontos />} />
      <Route path="/statistics" element={<Statistics />} />
      <Route path="/about" element={<About />} />
      <Route path="/rules" element={<Rules />} />
      <Route path="/edicao-youtube" element={<EdicaoYoutube />} />
      <Route path="/discord" element={<Discord />} />
      <Route path="/rankings" element={<Rankings />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/register-team" element={
        <ProtectedRoute>
          <RegisterTeamNew />
        </ProtectedRoute>
      } />
      <Route path="/payment" element={
        <ProtectedRoute>
          <Payment />
        </ProtectedRoute>
      } />
      <Route path="/renew" element={
        <ProtectedRoute>
          <RenewSubscription />
        </ProtectedRoute>
      } />
      <Route path="/perfil" element={
        <ProtectedRoute>
          <Perfil />
        </ProtectedRoute>
      } />
      <Route path="/stats" element={
        <ProtectedRoute>
          <StatsAnalyzer />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <GroupChat />
        </ProtectedRoute>
      } />
      <Route path="/knockout-chat" element={
        <ProtectedRoute>
          <KnockoutChat />
        </ProtectedRoute>
      } />
      <Route path="/knockout-chat/invite/:inviteCode" element={
        <ProtectedRoute>
          <KnockoutChatInvite />
        </ProtectedRoute>
      } />
      <Route path="/apostados" element={
        <ProtectedRoute>
          <Apostados />
        </ProtectedRoute>
      } />
      <Route path="/apostados-disponiveis" element={<ApostadosDisponiveis />} />
      <Route path="/apostados-ranking" element={<ApostadosRanking />} />
      <Route path="/meus-apostados" element={
        <ProtectedRoute>
          <MeusApostados />
        </ProtectedRoute>
      } />
      <Route path="/apostados/:roomId" element={
        <ProtectedRoute>
          <ApostadoRoom />
        </ProtectedRoute>
      } />
      <Route path="/admin/groups" element={
        <ProtectedRoute>
          <AdminGroups />
        </ProtectedRoute>
      } />
      <Route path="/admin/sorteio" element={
        <ProtectedRoute>
          <AdminSorteio />
        </ProtectedRoute>
      } />
      <Route path="/admin/matches" element={
        <ProtectedRoute>
          <AdminMatches />
        </ProtectedRoute>
      } />
      <Route path="/admin/test-data" element={
        <ProtectedRoute>
          <AdminTestData />
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute>
          <AdminContent />
        </ProtectedRoute>
      } />
      <Route path="/admin/rules" element={
        <ProtectedRoute>
          <AdminRules />
        </ProtectedRoute>
      } />
      <Route path="/admin/rankings" element={
        <ProtectedRoute>
          <AdminRankings />
        </ProtectedRoute>
      } />
      <Route path="/admin/discord" element={
        <ProtectedRoute>
          <AdminDiscord />
        </ProtectedRoute>
      } />
      <Route path="/admin/knockout" element={
        <ProtectedRoute>
          <AdminKnockout />
        </ProtectedRoute>
      } />
      <Route path="/admin/slots" element={
        <ProtectedRoute>
          <AdminSlotAssignment />
        </ProtectedRoute>
      } />
      <Route path="/admin/teams" element={
        <ProtectedRoute>
          <AdminTeamsNew />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/chat" element={
        <ProtectedRoute>
          <AdminChat />
        </ProtectedRoute>
      } />
      <Route path="/admin/knockout-chat" element={
        <ProtectedRoute>
          <AdminKnockoutChat />
        </ProtectedRoute>
      } />
      <Route path="/admin/diagnostic" element={
        <ProtectedRoute>
          <AdminDiagnostic />
        </ProtectedRoute>
      } />
      <Route path="/admin/reset" element={
        <ProtectedRoute>
          <AdminReset />
        </ProtectedRoute>
      } />
      <Route path="/admin/apostados" element={
        <ProtectedRoute>
          <AdminApostados />
        </ProtectedRoute>
      } />
      <Route path="/admin/ranking-apostados" element={
        <ProtectedRoute>
          <AdminRankingApostados />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute>
          <AdminPayments />
        </ProtectedRoute>
      } />
      <Route path="/admin/wallets" element={
        <ProtectedRoute>
          <AdminWallets />
        </ProtectedRoute>
      } />
      <Route path="/admin/central" element={
        <ProtectedRoute>
          <AdminCentral />
        </ProtectedRoute>
      } />
      <Route path="/knockout" element={<Knockout />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
